import { JSONSchema7Definition } from 'json-schema';

declare module 'json-schema' {
    export interface JSONSchema7 {
        enumNames?: Array<string | number>;
    }
}

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

export interface AnalyticsSchemaWidgetProperties {
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
}

interface Schema {
    [key: string]: JSONSchema7Definition;
}

// Function to generate schema for dynamic events
const generateDynamicEventSchema = (enumValues: string[]): JSONSchema7Definition => ({
    type: 'object',
    properties: {
        eventName: {
            type: 'string',
            title: 'Event Name',
            enum: enumValues,
            default: enumValues[0], // Default to the first enum value
        },
        customEventName: {
            type: 'string',
            title: 'Custom Event Name (Optional)',
        },
        analyticItems: {
            type: 'array',
            title: 'Page Analytics Items',
            items: {
                type: 'object',
                properties: {
                    name: {
                        type: 'string',
                        title: 'Name',
                    },
                    value: {
                        type: 'string',
                        title: 'Value',
                    },
                },
            },
        },
    },
});

// Function to generate the entire schema for the component
export const GenerateAnalyticsSchema = (enumValues: string[]) => {
    const schema: Schema = {
        useAnalytics: {
            type: 'boolean',
            title: 'Use analytics',
            description: 'Enable analytics to be tracked',
        },
        useLowerCase: {
            type: 'boolean',
            title: 'Use lower case value',
            description: 'Make values lower case',
        },
    };

    const dependencies: Schema = {
        useAnalytics: {
            oneOf: [
                {
                    properties: {
                        useAnalytics: { enum: [true] },
                        dynamicEvents: {
                            type: 'array',
                            title: 'Add Event with Custom Configuration',
                            items: generateDynamicEventSchema(enumValues),
                        },
                    },
                },
                {
                    properties: {
                        useAnalytics: { enum: [false] },
                    },
                },
            ],
        },
    };

    const group = {
        Analytics: ['useAnalytics', 'useLowerCase', 'dynamicEvents'],
    };

    const uiSchema = {
        useAnalytics: { 'ui:widget': 'CustomCheckbox' },
        useLowerCase: { 'ui:widget': 'CustomCheckbox' },
        dynamicEvents: {
            items: {
                eventName: { 'ui:widget': 'CustomSelect' },
                customEventName: { 'ui:widget': 'CustomTextInput' },
                analyticItems: {
                    items: {
                        name: { 'ui:widget': 'CustomTextInput' },
                        value: { 'ui:widget': 'ResourcePicker' },
                    },
                },
            },
        },
    };

    return {
        schema,
        group,
        uiSchema,
        dependencies,
    };
};
